import React from "react";
import { Activity, AlertTriangle, Check, CheckCircle2, ChevronLeft, ChevronRight, Eye } from "lucide-react";

import CollaborationDiscussButton from "../../Collaboration/CollaborationDiscussButton";
import { buildFindingTarget } from "../../Collaboration/collaboration-types";
import { FindingExplainEye } from "../../report/finding-explainability";
import {
  FINDING_FIELD_TO_EXPLAIN_KEY,
  type FindingAdditionalInfoBlock,
} from "../../../../helpers/findingsProvenance";
import { FindingEditControl } from "./FindingEditControl";
import {
  CATEGORY_ICON_MAP,
  ICON_MAP,
  REPORT_EDIT_CONTROLS,
  getAUAGestationalAge,
  getEFWValues,
  getGestationalAge,
  resolveFindingDbId,
  type Finding,
} from "../utils/utils";

type FindingCardProps = {
  finding: Finding;
  studyInstanceUid: string;
  activeSeries: string;
  canUpdateActiveSeries: boolean;
  findingSeriesIndex: Record<string, number>;
  findingsByType: Record<string, Finding[]>;
  formatEditOptionLabel: (label: string) => string;
  isSelected: boolean;
  reportEdits: Record<string, string>;
  selectedCategories: string[];
  showReportMode: boolean;
  onEditChange: (finding: Finding, backendKey: string, value: string) => void;
  onNavigateSeries: (finding: Finding, direction: "prev" | "next") => void;
  onToggle: (findingId: string) => void;
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case "NORMAL":
      return {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700",
        icon: <Check size={12} className="text-emerald-600 dark:text-emerald-400" />,
        badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
        dot: "bg-emerald-500",
      };
    case "ABNORMAL":
      return {
        color: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-700",
        icon: <AlertTriangle size={12} className="text-red-600 dark:text-red-400" />,
        badge: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
        dot: "bg-red-500",
      };
    case "OBSERVATION":
      return {
        color: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700",
        icon: <Eye size={12} className="text-blue-600 dark:text-blue-400" />,
        badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
        dot: "bg-blue-500",
      };
    default:
      return {
        color: "bg-slate-50 text-slate-700 border-slate-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600",
        icon: <Eye size={12} className="text-slate-600 dark:text-gray-400" />,
        badge: "bg-slate-100 text-slate-800 dark:bg-gray-700 dark:text-gray-300",
        dot: "bg-slate-500 dark:bg-gray-500",
      };
  }
};

const getSpecificIcon = (fieldKey: string, rawData: any): string | null => {
  switch (fieldKey) {
    case "presentation":
      if (rawData.presentation === "cephalic") return ICON_MAP.cephalic;
      if (rawData.presentation === "breech") return ICON_MAP.breech;
      if (rawData.presentation === "transverse") return ICON_MAP.transverse;
      return null;
    case "placenta_localization":
      if (rawData.placenta_localization === "anterior") return ICON_MAP.anterior;
      if (rawData.placenta_localization === "posterior") return ICON_MAP.posterior;
      if (rawData.placenta_localization === "fundal") return ICON_MAP.fundal;
      return null;
    case "placenta_distance_to_cervix_class":
      if (rawData.placenta_distance_to_cervix_class === "low-lying") return ICON_MAP.lowLying;
      return null;
    case "sex_prediction":
      return ICON_MAP.fetalSex;
    case "meas_hc":
      return ICON_MAP.headCircumference;
    case "meas_bpd":
      return ICON_MAP.biparietalDiameter;
    case "meas_ac":
      return ICON_MAP.abdominal;
    case "meas_fl":
      return ICON_MAP.femur;
    case "pre_eclampsia":
      return ICON_MAP.preeclampsia;
    case "gestational_diabetes":
      return ICON_MAP.gestationalDiabetes;
    case "heart_rate":
      return ICON_MAP.fetalHeart;
    case "pred_edd":
      return ICON_MAP.gestationalAge;
    default:
      return null;
  }
};

const SectionIcon = ({ finding }: { finding: Finding }) => {
  const specificIcon = getSpecificIcon(finding.fieldKey, finding.rawData);

  if (specificIcon) {
    return (
      <img
        src={specificIcon}
        alt={`${finding.fieldKey} icon`}
        className="object-contain"
      />
    );
  }

  const icon = CATEGORY_ICON_MAP[finding.category];
  if (!icon) {
    return <Activity size={80} className="text-slate-600 dark:text-gray-400" />;
  }

  return <img src={icon} alt={finding.category} className="object-contain" />;
};

export const FindingCard = ({
  finding,
  studyInstanceUid,
  activeSeries,
  canUpdateActiveSeries,
  findingSeriesIndex,
  findingsByType,
  formatEditOptionLabel,
  isSelected,
  reportEdits,
  selectedCategories,
  showReportMode,
  onEditChange,
  onNavigateSeries,
  onToggle,
}: FindingCardProps) => {
  const statusConfig = getStatusConfig(finding.status);
  const isUnavailable = !finding.isAvailable;
  const isInReport = finding.isInReport;
  const isCalculatedValue =
    finding.category === "Estimated Fetal Weight" ||
    finding.category === "Gestational Age";
  const isEditableFinding = Boolean(REPORT_EDIT_CONTROLS[finding.fieldKey]);
  const isActionableInReportMode =
    showReportMode &&
    canUpdateActiveSeries &&
    (isEditableFinding || (!isUnavailable && !isCalculatedValue));

  const typeKey = `${finding.fieldKey}_${finding.category}`;
  const allSeriesForFinding = findingsByType[typeKey] || [];
  const filteredSeriesFindings = allSeriesForFinding.filter((seriesFinding) => {
    if (selectedCategories.length > 0) {
      return selectedCategories.includes(seriesFinding.category);
    }
    return true;
  });
  const hasMultipleSeries = filteredSeriesFindings.length > 1;
  const currentSeriesIndex = findingSeriesIndex[typeKey] || 0;

  const gaData = getGestationalAge(finding);
  const efwValues = getEFWValues(finding);
  const auaData = getAUAGestationalAge(finding);
  const findingDbId = resolveFindingDbId(finding);

  const explainKey = FINDING_FIELD_TO_EXPLAIN_KEY[finding.fieldKey];
  const explainBlock = explainKey
    ? ((finding.rawData?.additional_info as
        | Record<string, FindingAdditionalInfoBlock>
        | undefined)?.[explainKey])
    : undefined;

  return (
    <div
      className={`group relative bg-white dark:bg-gray-800 rounded-xl border transition-all duration-200 hover:shadow-lg h-full ${showReportMode
        ? isSelected
          ? "border-blue-300 dark:border-blue-600 bg-blue-50/30 dark:bg-blue-900/20 shadow-md ring-1 ring-blue-200 dark:ring-blue-600"
          : isInReport
            ? "border-green-300 dark:border-green-600 bg-green-50/10 dark:bg-green-900/10 cursor-pointer"
            : isCalculatedValue && !isEditableFinding
              ? "border-orange-200 dark:border-orange-700 bg-orange-50/10 dark:bg-orange-900/10 cursor-not-allowed"
              : "border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600 cursor-pointer"
        : isInReport
          ? "border-green-300 dark:border-green-600 bg-green-50/10 dark:bg-green-900/10 hover:border-green-400 dark:hover:border-green-500"
          : "border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600"
        } ${isUnavailable && !(showReportMode && isEditableFinding) ? "opacity-60" : ""} ${isCalculatedValue && showReportMode && !isEditableFinding ? "opacity-70" : ""}`}
      onClick={() => isActionableInReportMode && onToggle(finding.id)}
    >
      {isUnavailable && (
        <div className="absolute inset-0 bg-slate-100/20 dark:bg-gray-900/20 rounded-xl z-5 pointer-events-none" />
      )}

      {hasMultipleSeries && !showReportMode && activeSeries === "All" && (
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <button
            onClick={(event) => {
              event.stopPropagation();
              onNavigateSeries(finding, "prev");
            }}
            className="w-8 h-8 rounded-full bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 shadow-sm hover:bg-slate-50 dark:hover:bg-gray-600 flex items-center justify-center transition-all"
          >
            <ChevronLeft size={16} className="text-slate-600 dark:text-gray-400" />
          </button>
          <span className="text-xs font-medium text-slate-600 dark:text-gray-400 bg-white dark:bg-gray-700 px-2 py-1 rounded-full border border-slate-200 dark:border-gray-600">
            {finding.rawData?.serie?.type || finding.serieId} ({currentSeriesIndex + 1}/{filteredSeriesFindings.length})
          </span>
          <button
            onClick={(event) => {
              event.stopPropagation();
              onNavigateSeries(finding, "next");
            }}
            className="w-8 h-8 rounded-full bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 shadow-sm hover:bg-slate-50 dark:hover:bg-gray-600 flex items-center justify-center transition-all"
          >
            <ChevronRight size={16} className="text-slate-600 dark:text-gray-400" />
          </button>
        </div>
      )}

      {isActionableInReportMode && (
        <div className="absolute top-4 right-4 z-10">
          <div
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? "bg-blue-600 border-blue-600" : "border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 group-hover:border-blue-400 dark:group-hover:border-blue-500"
              }`}
          >
            {isSelected && <Check size={12} className="text-white" />}
          </div>
        </div>
      )}

      {showReportMode && isCalculatedValue && !isEditableFinding && canUpdateActiveSeries && (
        <div className="absolute top-4 right-4 z-10">
          <div className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-md">
            <span className="text-xs font-medium text-orange-700 dark:text-orange-400">Auto-calculated</span>
          </div>
        </div>
      )}

      <div className={`p-6 h-full flex flex-col ${hasMultipleSeries && !showReportMode && activeSeries === "All" ? "pt-16" : ""}`}>
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white leading-tight line-clamp-2 mb-2">
              {finding.title}
            </h3>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {explainBlock && explainKey && (
              <div onClick={(event) => event.stopPropagation()}>
                <FindingExplainEye
                  findingKey={explainKey}
                  labelKey={finding.title}
                  block={explainBlock}
                  studyUID={studyInstanceUid}
                  size={16}
                />
              </div>
            )}
            {findingDbId != null && (
              <div onClick={(event) => event.stopPropagation()}>
                <CollaborationDiscussButton
                  variant="card"
                  target={buildFindingTarget(
                    findingDbId,
                    finding.title,
                    finding.result,
                    finding.fieldKey
                  )}
                />
              </div>
            )}
            <div className={`w-3 h-3 rounded-full ${statusConfig.dot}`} />
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${statusConfig.color}`}
          >
            {statusConfig.icon}
            {finding.status}
          </span>
          {isInReport && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700">
              <CheckCircle2 size={12} className="text-green-600 dark:text-green-400" />
              In Report
            </span>
          )}
        </div>

        <div className="flex gap-4 mb-4 flex-1">
          <div className="flex-[0.4] flex flex-col justify-center">
            <div className="mb-3">
              <span className="text-sm font-medium text-slate-600 dark:text-gray-400 block mb-1">Result:</span>
              {showReportMode && canUpdateActiveSeries && isEditableFinding ? (
                <FindingEditControl
                  finding={finding}
                  reportEdits={reportEdits}
                  formatOptionLabel={formatEditOptionLabel}
                  onChange={onEditChange}
                />
              ) : (
                <span className={`text-lg font-bold break-words leading-tight whitespace-pre-line ${isUnavailable ? "text-slate-400 dark:text-gray-500" : "text-slate-900 dark:text-white"}`}>
                  {finding.result}
                </span>
              )}
            </div>

            {gaData.hasGA && finding.category === "Biometry Measurements" && (
              <div className="mb-3">
                <div className="inline-flex items-center gap-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                    GA: {gaData.weeks}w {gaData.days}d
                  </span>
                </div>
              </div>
            )}

            {activeSeries !== "Report" && !finding.isReportSpecific && efwValues.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {efwValues.map((efw, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700"
                  >
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span className="text-xs font-medium text-green-700 dark:text-green-300">
                      {efw.label}: {efw.value}g
                    </span>
                  </div>
                ))}
              </div>
            )}

            {auaData.hasAUA && (
              <div className="mb-3">
                <div className="inline-flex items-center gap-2 px-2 py-1 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-md">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                  <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                    AUA: {auaData.weeks}w {auaData.days}d
                  </span>
                </div>
              </div>
            )}

            <p className={`text-xs leading-relaxed line-clamp-4 ${isUnavailable ? "text-slate-400 dark:text-gray-500" : "text-slate-600 dark:text-gray-400"}`}>
              {finding.description}
            </p>
          </div>

          <div className="flex-[0.6] flex items-center justify-center">
            <div className="w-full h-32 flex items-center justify-center p-4">
              <SectionIcon finding={finding} />
            </div>
          </div>
        </div>

        <div className="pt-4 mt-auto">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${statusConfig.badge}`}
          >
            {finding.category}
          </span>
        </div>
      </div>
    </div>
  );
};
